import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight } from 'lucide-react';
import { colors, typography, spacing, borderRadius, animation, media } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';
import { useUsageStats, usePlans } from '../../hooks/useSubscription';
import { SubscriptionService } from '../../services/subscription.service';
import { Modal } from '../ui/Modal';
import { useToast } from '../Toast';

interface UserProfileWidgetProps {
  onNavigateToSettings?: () => void;
}

export const UserProfileWidget: React.FC<UserProfileWidgetProps> = ({ onNavigateToSettings }) => {
  const { t } = useTranslation();
  const { user, credits, isAuthenticated } = useAuth();
  const { data: usageStats, isLoading } = useUsageStats();
  const { data: allPlans } = usePlans();
  const toast = useToast();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentPlanType = usageStats?.plan?.type ?? 1;
  const upgradablePlans = (allPlans || [])
    .filter(p => (p.type ?? 0) > currentPlanType && (p.type ?? 0) <= 6)
    .sort((a, b) => (a.type ?? 0) - (b.type ?? 0));
  const canUpgrade = upgradablePlans.length > 0;

  const handleUpgrade = async (planId: number) => {
    setIsCheckoutLoading(true);
    try {
      const { url } = await SubscriptionService.createCheckoutSession(planId, 'month');
      window.location.href = url;
    } catch (err) {
      console.error('[upgrade] failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsCheckoutLoading(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isDropdownOpen]);

  if (!isAuthenticated || !user) {
    return null;
  }

  const getInitials = () => {
    if (user.firstName && user.lastName) return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    if (user.firstName) return user.firstName.substring(0, 2).toUpperCase();
    return user.email?.substring(0, 2).toUpperCase() || 'U';
  };

  const getDisplayName = () => {
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    return user.email?.split('@')[0] || 'User';
  };

  const getPlanColor = () => {
    if (!usageStats?.plan?.name) return '#a78bfa';
    const p = usageStats.plan.name.toLowerCase();
    if (p.includes('enterprise') || p.includes('professional')) return 'rgba(168, 85, 247, 1)';
    if (p.includes('personal') || p.includes('guardian')) return 'rgba(139, 92, 246, 1)';
    return '#a78bfa';
  };

  const creditsData = usageStats?.credits;
  const creditsRemaining = creditsData?.remaining ?? credits;

  const creditsColor = creditsRemaining > 10
    ? 'rgba(0, 191, 166, 0.8)'
    : creditsRemaining >= 5
      ? 'rgba(255, 165, 0, 0.8)'
      : 'rgba(230, 57, 70, 0.8)';

  return (
    <Container ref={containerRef}>
      {canUpgrade && (
        <UpgradeBtn
          onClick={(e) => { e.stopPropagation(); setIsUpgradeOpen(true); }}
          aria-label={t('profile.upgrade', 'Upgrade')}
        >
          <Sparkles size={14} strokeWidth={2.25} />
          <span>{t('profile.upgrade', 'Upgrade')}</span>
        </UpgradeBtn>
      )}

      <ProfileTrigger onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
        <Avatar>{getInitials()}</Avatar>
        <TriggerName>{getDisplayName()}</TriggerName>
        <CreditsBadge>{t('profile.creditsLabel', { count: creditsRemaining })}</CreditsBadge>
        <DropdownArrow>▾</DropdownArrow>
        {usageStats?.plan?.name && (
          <PlanBadge color={getPlanColor()}>{usageStats.plan.name}</PlanBadge>
        )}
      </ProfileTrigger>

      <Dropdown isOpen={isDropdownOpen}>
        {/* Top: user info */}
        <Header>
          <AvatarLg>{getInitials()}</AvatarLg>
          <UserInfo>
            <Name>{getDisplayName()}</Name>
            <Email>{user.email}</Email>
            <PlanRow>
              <PlanLabel color={getPlanColor()}>{usageStats?.plan?.name || t('profile.planFree')}</PlanLabel>
              <PlanPrice>{usageStats?.plan?.priceAmount ? t('profile.pricePerMonth', { price: `$${usageStats.plan.priceAmount}` }) : t('profile.planFree')}</PlanPrice>
              {usageStats?.subscription && (
                <Status active={usageStats.subscription.isActive}>
                  {usageStats.subscription.isActive ? t('common.active') : t('common.inactive')}
                </Status>
              )}
            </PlanRow>
          </UserInfo>
        </Header>

        <Sep />

        {/* Credits — prominent, full width */}
        <Block>
          <ColTitle>{t('profile.creditsTitle')}</ColTitle>
          {isLoading ? (
            <Muted>{t('common.loading')}</Muted>
          ) : (
            <>
              <CreditsNum>
                <Big style={{ color: creditsColor }}>{creditsRemaining}</Big>
                <Small>{t('profile.remaining')}</Small>
              </CreditsNum>
              {creditsRemaining === 0 && (
                <Warn>{t('profile.noCreditsLeft')}</Warn>
              )}
              {creditsRemaining > 0 && creditsRemaining < 5 && (
                <Warn>{t('profile.creditsRunningLow')}</Warn>
              )}
            </>
          )}
        </Block>

        <Sep />

        {/* Cost per analysis — the highlight */}
        <Block>
          <ColTitle>{t('profile.costPerAnalysis')}</ColTitle>
          <CostGrid>
            <CostItem><CostName>{t('profile.costText')}</CostName><CostVal>1<Unit>cr</Unit></CostVal></CostItem>
            <CostItem><CostName>{t('profile.costUrl')}</CostName><CostVal>1<Unit>cr</Unit></CostVal></CostItem>
            <CostItem><CostName>{t('profile.costImage')}</CostName><CostVal>2<Unit>cr</Unit></CostVal></CostItem>
            <CostItem><CostName>{t('profile.costAudio')}</CostName><CostVal>3<Unit>cr</Unit></CostVal></CostItem>
            <CostItem><CostName>{t('profile.costVideo')}</CostName><CostVal>5<Unit>cr</Unit></CostVal></CostItem>
          </CostGrid>
        </Block>

        <Sep />

        {/* Footer — settings */}
        <Footer>
          <SettingsBtn onClick={onNavigateToSettings}>⚙️ {t('profile.settingsButton')}</SettingsBtn>
        </Footer>
      </Dropdown>

      <Modal
        isOpen={isUpgradeOpen}
        onClose={() => setIsUpgradeOpen(false)}
        title={t('profile.upgradeYourPlan', 'Upgrade your plan')}
        maxWidth="720px"
      >
        <UpgradeIntro>
          {t('profile.upgradeIntro', 'Choose a plan with more credits, more storage, and access to premium features.')}
        </UpgradeIntro>
        <PlansGrid>
          {upgradablePlans.map(plan => (
            <PlanCard key={plan.id}>
              <PlanCardHeader>
                <PlanCardName>{plan.name}</PlanCardName>
                <PlanCardPrice>
                  €{plan.priceMonthly ?? plan.priceUsd ?? plan.priceAmount ?? 0}
                  <small>/mo</small>
                </PlanCardPrice>
              </PlanCardHeader>
              <PlanCardSpecs>
                <Spec><SpecVal>{plan.creditsPerCycle}</SpecVal><SpecLbl>credits/lună</SpecLbl></Spec>
                <Spec><SpecVal>{plan.maxImages ?? '—'}</SpecVal><SpecLbl>imagini</SpecLbl></Spec>
                <Spec><SpecVal>{plan.maxVideoMinutes ?? '—'}m</SpecVal><SpecLbl>video</SpecLbl></Spec>
                <Spec>
                  <SpecVal>{(plan.storageLimitGb ?? 0) < 0 ? '∞' : `${plan.storageLimitGb}GB`}</SpecVal>
                  <SpecLbl>storage</SpecLbl>
                </Spec>
              </PlanCardSpecs>
              <PlanCardCta
                onClick={() => handleUpgrade(plan.id)}
                disabled={isCheckoutLoading}
              >
                {isCheckoutLoading ? t('common.loading') : t('profile.upgradeTo', { plan: plan.name, defaultValue: `Upgrade la ${plan.name}` })}
                <ArrowRight size={14} strokeWidth={2.25} />
              </PlanCardCta>
            </PlanCard>
          ))}
        </PlansGrid>
        <UpgradeFooter>
          {t('profile.upgradeYearlyHint', 'Yearly billing saves you 17% (the equivalent of 2 free months) — you can switch from Settings after upgrading.')}
        </UpgradeFooter>
      </Modal>
    </Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${spacing.sm}px;
`;

const ProfileTrigger = styled.div`
  display: flex; align-items: center; gap: ${spacing.sm}px;
  max-width: 480px; width: auto;
  padding: ${spacing.xs}px ${spacing.md}px;
  background: #050510; border: 1px solid rgba(139,92,246,0.2);
  border-radius: ${borderRadius.md}px; cursor: pointer;
  transition: all ${animation.duration.normal} ${animation.easing.default};
  [data-theme="light"] & { background: rgba(139,92,246,0.03); border-color: rgba(139,92,246,0.15); }
  &:hover { background: rgba(139,92,246,0.1); border-color: rgba(139,92,246,0.4); }
`;

const Avatar = styled.div`
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, rgba(139,92,246,0.8), rgba(168,85,247,0.9));
  display: flex; align-items: center; justify-content: center;
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold}; color: #fff;
`;

const AvatarLg = styled(Avatar)`width: 40px; height: 40px; font-size: ${typography.fontSize.base};`;

const TriggerName = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium}; color: #fff;
  flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
  @media (max-width: 768px) { display: none; }
`;

const CreditsBadge = styled.span`
  padding: 2px 8px; background: rgba(0,191,166,0.15); border: 1px solid rgba(0,191,166,0.3);
  border-radius: 12px; font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs}; font-weight: ${typography.fontWeight.semibold};
  color: ${colors.honestTeal}; white-space: nowrap;
  @media (max-width: 768px) { display: none; }
`;

const DropdownArrow = styled.span`
  font-size: ${typography.fontSize.xs}; color: #fff;
  transition: transform ${animation.duration.normal} ${animation.easing.default};
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
  @media (max-width: 768px) { display: none; }
`;

const PlanBadge = styled.span<{ color: string }>`
  padding: 2px 8px; background: ${p => p.color}20; border: 1px solid ${p => p.color}40;
  border-radius: 12px; font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs}; font-weight: ${typography.fontWeight.semibold};
  color: ${p => p.color}; text-transform: uppercase; letter-spacing: 0.5px;
  @media (max-width: 768px) { display: none; }
`;

// ─── Dropdown ────────────────────────────────────────────────────

const Dropdown = styled.div<{ isOpen?: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 480px;
  background: rgba(5,5,16,0.97);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(139,92,246,0.2);
  border-radius: ${borderRadius.lg}px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.5);
  opacity: ${p => p.isOpen ? 1 : 0};
  visibility: ${p => p.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${p => p.isOpen ? '0' : '-8px'});
  transition: all 0.2s ease;
  z-index: 100;
  [data-theme="light"] & {
    background: rgba(255,255,255,0.98);
    border-color: rgba(139,92,246,0.15);
    box-shadow: 0 10px 40px rgba(0,0,0,0.12);
  }

  ${media.maxMobile} { width: calc(100vw - 32px); right: -16px; }
`;

const Header = styled.div`
  display: flex; align-items: center; gap: ${spacing.md}px;
  padding: ${spacing.md}px ${spacing.lg}px;
`;

const UserInfo = styled.div`flex: 1; min-width: 0;`;

const Name = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold}; color: #fff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;

const Email = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: rgba(255,255,255,0.55); overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; margin-top: 1px;
  [data-theme="light"] & { color: #6B7280; }
`;

const PlanRow = styled.div`
  display: flex; align-items: center; gap: 8px; margin-top: 4px;
`;

const PlanLabel = styled.span<{ color: string }>`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold}; color: ${p => p.color};
`;

const PlanPrice = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: 11px;
  color: rgba(255,255,255,0.4);
  [data-theme="light"] & { color: #9CA3AF; }
`;

const Status = styled.span<{ active: boolean }>`
  font-family: ${typography.fontFamily.primary}; font-size: 10px;
  padding: 1px 6px; border-radius: 8px;
  background: ${p => p.active ? 'rgba(0,191,166,0.12)' : 'rgba(248,113,113,0.12)'};
  color: ${p => p.active ? 'rgba(0,191,166,0.9)' : 'rgba(248,113,113,0.9)'};
  [data-theme="light"] & {
    color: ${p => p.active ? '#059669' : '#dc2626'};
    background: ${p => p.active ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)'};
  }
`;

const Sep = styled.div`
  height: 1px; background: rgba(139,92,246,0.1); margin: 0 ${spacing.md}px;
  [data-theme="light"] & { background: rgba(139,92,246,0.08); }
`;

// ─── Two columns ─────────────────────────────────────────────────

const TwoCol = styled.div`
  display: flex; padding: ${spacing.md}px ${spacing.lg}px; gap: 0;
  ${media.maxMobile} { flex-direction: column; gap: ${spacing.md}px; }
`;

const Col = styled.div`flex: 1; min-width: 0;`;

const ColSep = styled.div`
  width: 1px; background: rgba(139,92,246,0.1); margin: 0 ${spacing.md}px; flex-shrink: 0;
  [data-theme="light"] & { background: rgba(139,92,246,0.08); }
  ${media.maxMobile} { display: none; }
`;

const ColTitle = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: 10px;
  font-weight: ${typography.fontWeight.semibold}; color: rgba(255,255,255,0.4);
  text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 8px;
  [data-theme="light"] & { color: #9CA3AF; }
`;

const CreditsNum = styled.div`
  display: flex; align-items: baseline; gap: 4px; margin-bottom: 6px;
`;

const Big = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: 24px;
  font-weight: ${typography.fontWeight.bold}; color: #fff; line-height: 1;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;

const Small = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: rgba(255,255,255,0.35);
  [data-theme="light"] & { color: #9CA3AF; }
`;


const Warn = styled.div`
  font-size: 10px; color: rgba(248,113,113,0.85); margin-top: 3px;
  [data-theme="light"] & { color: #dc2626; }
`;

const Limits = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
`;

const Lim = styled.div`
  display: flex; align-items: center; gap: 4px;
  padding: 4px 6px; border-radius: 6px;
  background: rgba(139,92,246,0.04);
  [data-theme="light"] & { background: rgba(139,92,246,0.03); }
`;

const LimIcon = styled.span`font-size: 12px; flex-shrink: 0;`;

const LimVal = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: 13px;
  font-weight: ${typography.fontWeight.semibold}; color: #fff;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;

const LimLbl = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: 9px;
  color: rgba(255,255,255,0.35); margin-left: auto;
  [data-theme="light"] & { color: #9CA3AF; }
`;

// ─── Footer ──────────────────────────────────────────────────────

const Footer = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 8px;
  padding: 8px ${spacing.lg}px;
  border-top: 1px solid rgba(139,92,246,0.08);
`;

const Costs = styled.div`
  display: flex; align-items: center; gap: 5px; flex-wrap: wrap;
`;

const CostTag = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 6px; background: rgba(139,92,246,0.05);
  border: 1px solid rgba(139,92,246,0.1); border-radius: 8px;
  font-family: ${typography.fontFamily.primary}; font-size: 10px;
  color: rgba(255,255,255,0.5);
  [data-theme="light"] & { background: rgba(139,92,246,0.03); border-color: rgba(139,92,246,0.08); color: #6B7280; }
`;

const C = styled.span`
  font-weight: ${typography.fontWeight.bold}; color: rgba(139,92,246,0.8);
  [data-theme="light"] & { color: rgba(139,92,246,1); }
`;

const Block = styled.div`
  padding: ${spacing.md}px ${spacing.lg}px;
`;

const CostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(58px, 1fr));
  gap: 6px;
`;

const CostItem = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  padding: 9px 4px; border-radius: 10px;
  background: rgba(139,92,246,0.05);
  border: 1px solid rgba(139,92,246,0.12);
  [data-theme="light"] & { background: rgba(139,92,246,0.03); border-color: rgba(139,92,246,0.1); }
`;

const CostEmoji = styled.span`font-size: 17px; line-height: 1;`;

const CostName = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: 10px;
  font-weight: ${typography.fontWeight.semibold}; text-transform: uppercase;
  letter-spacing: 0.4px; color: rgba(255,255,255,0.45);
  [data-theme="light"] & { color: #9CA3AF; }
`;

const CostVal = styled.span`
  display: inline-flex; align-items: baseline; gap: 1px;
  font-family: ${typography.fontFamily.primary}; font-size: 16px;
  font-weight: ${typography.fontWeight.bold}; color: ${colors.honestTeal}; line-height: 1;
`;

const Unit = styled.span`
  font-size: 8px; font-weight: 500; color: rgba(255,255,255,0.3);
  [data-theme="light"] & { color: #9CA3AF; }
`;

const SettingsBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 5px 12px; background: rgba(139,92,246,0.06);
  border: 1px solid rgba(139,92,246,0.15); border-radius: ${borderRadius.sm}px;
  font-family: ${typography.fontFamily.primary}; font-size: 12px;
  font-weight: ${typography.fontWeight.medium}; color: #fff; cursor: pointer;
  transition: all 0.2s;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; background: rgba(139,92,246,0.04); }
  &:hover { background: rgba(139,92,246,0.12); border-color: rgba(139,92,246,0.3); }
`;

const Muted = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: rgba(255,255,255,0.4); padding: 4px 0;
  [data-theme="light"] & { color: #9CA3AF; }
`;

// ─── Upgrade button + modal ─────────────────────────────────────

const UpgradeBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; cursor: pointer;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  border: none; border-radius: ${borderRadius.md}px;
  color: #fff; font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.35);
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(124, 58, 237, 0.5); }
  ${media.maxMobile} {
    padding: 6px 10px;
    & > span { display: none; }
  }
`;

const UpgradeIntro = styled.p`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: var(--fg-secondary); line-height: 1.5; margin: 0 0 ${spacing.lg}px 0;
`;

const PlansGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing.md}px;
`;

const PlanCard = styled.div`
  display: flex; flex-direction: column; gap: ${spacing.md}px;
  padding: ${spacing.lg}px;
  background: var(--bg-surface); border: 1px solid var(--border-default);
  border-radius: ${borderRadius.md}px;
  transition: all 0.15s;
  &:hover { border-color: var(--accent-border); box-shadow: 0 4px 12px rgba(124,58,237,0.08); }
`;

const PlanCardHeader = styled.div`
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
`;

const PlanCardName = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold}; color: var(--fg-primary);
`;

const PlanCardPrice = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold}; color: ${colors.neonViolet};
  & small { font-size: ${typography.fontSize.xs}; font-weight: ${typography.fontWeight.regular}; color: var(--fg-muted); margin-left: 2px; }
`;

const PlanCardSpecs = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
`;

const Spec = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  padding: 6px 8px; background: var(--bg-hover); border-radius: ${borderRadius.sm}px;
`;

const SpecVal = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold}; color: var(--fg-primary);
`;

const SpecLbl = styled.span`
  font-family: ${typography.fontFamily.primary}; font-size: 10px;
  color: var(--fg-muted); text-transform: uppercase; letter-spacing: 0.4px;
`;

const PlanCardCta = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 12px; cursor: pointer;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  border: none; border-radius: ${borderRadius.sm}px;
  color: #fff; font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  transition: all 0.2s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(124,58,237,0.4); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const UpgradeFooter = styled.div`
  margin-top: ${spacing.lg}px; padding-top: ${spacing.md}px;
  border-top: 1px solid var(--border-subtle);
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.xs};
  color: var(--fg-muted); line-height: 1.5; text-align: center;
`;

export default UserProfileWidget;
